import clsx from 'clsx';
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { ErrorBoundary } from 'react-error-boundary';

//hooks
import useSession from '@/utils/hooks/user/useSession';
import useRouterQuery from '@/utils/hooks/utils/useRouterQuery';
import PageAccessRightsMiddleware from '@/utils/hooks/middleware/usePageAccessRights';
import PageNewFeatureVisitedHandler from '@/utils/hooks/user/useNewFeatureNotifications';
import { ToastsProvider } from '@/utils/hooks/context/toast/useToastsContext';

// Components
import SWRConfigWrapper from '@/components/layouts/SWRConfigWrapper';
import ErrorFallbackGlobal from '@/components/layouts/ErrorFallbacks/ErrorFallbackGlobal';
import LoaderPage from '@/components/base/Loader/LoaderPage';
import GlobalBanners from '@/components/composite/Banners/GlobalBanner';
import BackgroundLayers from '@/components/layouts/BackgroundLayers/BackgroundLayers';
import EmailUpdateConfirmationSuccessModal from '@/components/pages/settings/EmailUpdateConfirmationSuccessModal';
import ToastsWrapper from '@/components/base/Toast/ToastsWrapper';
import GlobalEventsListener from '@/components/composite/Listeners/GlobalEventsListener';

// Types
import type { ComponentPropsWithoutRef } from 'react';

const ContentWrapper = ({ className, children, ...restProps }: ComponentPropsWithoutRef<'div'>) => {
    const { loading, session } = useSession();
    const router = useRouter();
    const { deleteQuery } = useRouterQuery();
    const { email_updated, invitation } = router.query;

    useEffect(() => {
        if (invitation) {
            const newUrl = deleteQuery(['invitation', 'email']);
            router.replace(newUrl);
        }
    }, [deleteQuery, invitation, router]);

    if (loading || !session) {
        return <LoaderPage />;
    }
    return (
        <PageAccessRightsMiddleware>
            <ToastsProvider>
                <PageNewFeatureVisitedHandler>
                    <div className={clsx('relative flex h-screen', className)} {...restProps}>
                        {email_updated ? <EmailUpdateConfirmationSuccessModal /> : null}
                        <GlobalEventsListener />
                        {children}
                    </div>
                    {session?.sign_up_step === 'sign_up_done' ? <ToastsWrapper /> : null}
                </PageNewFeatureVisitedHandler>
            </ToastsProvider>
        </PageAccessRightsMiddleware>
    );
};

const LayoutLogged = ({ children, className, ...restProps }: ComponentPropsWithoutRef<'div'>) => {
    return (
        <SWRConfigWrapper>
            <ContentWrapper className={className} {...restProps}>
                <div className="scrollbar h-full w-full overflow-x-hidden" id="wrapper-logged">
                    {children}
                </div>
                <div className="absolute inset-0 -z-1 overflow-hidden">
                    <BackgroundLayers className="absolute inset-0 h-full min-h-full w-full" />
                </div>
            </ContentWrapper>
        </SWRConfigWrapper>
    );
};

//Logged Menu section
const LeftSide = ({ children, className, ...restProps }: ComponentPropsWithoutRef<'div'>) => (
    <div
        role="navigation"
        className={clsx(
            'fixed left-m right-m top-12 z-20 rounded-lg backdrop-blur-sm md:bottom-m md:right-unset md:top-m md:bg-none md:backdrop-filter-none',
            'bg-white/80 dark:bg-icoDarkMode-anthracite/80',
            'firefox:bg-gradient-to-tr firefox:from-icoGrayFirefox-300 firefox:to-icoWhiteFirefox dark:firefox:from-icoDarkMode-deepBlack dark:firefox:to-icoDarkMode-anthracite',
            className
        )}
        {...restProps}>
        {children}
    </div>
);
LayoutLogged.LeftSide = LeftSide;

//Logged List social profiles
const RightSide = ({ children, ...restProps }: ComponentPropsWithoutRef<'div'>) => (
    <div className="fixed z-20 hidden md:bottom-m md:right-m md:top-m md:flex" {...restProps}>
        {children}
    </div>
);
LayoutLogged.RightSide = RightSide;

//Logged Body
const Body = ({
    children,
    className,
    bodyWrapperClass,
    withGlobalBanner = true,
    ...restProps
}: ComponentPropsWithoutRef<'div'> & {
    bodyWrapperClass?: string;
    withGlobalBanner?: boolean;
}) => (
    <ErrorBoundary FallbackComponent={ErrorFallbackGlobal}>
        <div
            className={clsx(
                'flex min-h-full w-full px-m pb-m md:pl-36 md:pr-[5.688rem] xl:px-48',
                bodyWrapperClass
            )}>
            <div className="mt-36 flex w-full flex-1 flex-col md:mt-0">
                {withGlobalBanner ? <GlobalBanners /> : null}
                <div
                    className={clsx(
                        'mx-auto flex min-h-full w-full max-w-[100rem] flex-col',
                        className
                    )}
                    {...restProps}>
                    {children}
                </div>
            </div>
        </div>
    </ErrorBoundary>
);
LayoutLogged.Body = Body;

// Logged layout header
const Header = ({ children, className, ...restProps }: ComponentPropsWithoutRef<'div'>) => {
    return (
        <header className={clsx('z-10 mb-5 pb-5 pt-10', className)} {...restProps}>
            {children}
        </header>
    );
};
LayoutLogged.Header = Header;

export default LayoutLogged;
